import { create } from 'zustand';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

/** Hex da superfície da topbar (tokens HeroUI) — usado na status bar do PWA. */
const THEME_COLOR: Record<Theme, string> = {
  light: '#ffffff',
  dark: '#18181b',
};

/** Opaca no iOS PWA — `black-translucent` deixa frost/blur em cima da topbar. */
const STATUS_BAR_STYLE: Record<Theme, 'default' | 'black'> = {
  light: 'default',
  dark: 'black',
};

function readStoredTheme(): Theme {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return 'dark';
}

function syncPwaChromeMeta(theme: Theme) {
  if (typeof document === 'undefined') return;

  let themeColor = document.querySelector('meta[name="theme-color"]');
  if (!themeColor) {
    themeColor = document.createElement('meta');
    themeColor.setAttribute('name', 'theme-color');
    document.head.appendChild(themeColor);
  }
  themeColor.setAttribute('content', THEME_COLOR[theme]);

  let statusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (!statusBar) {
    statusBar = document.createElement('meta');
    statusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
    document.head.appendChild(statusBar);
  }
  statusBar.setAttribute('content', STATUS_BAR_STYLE[theme]);
}

export function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
  localStorage.setItem(STORAGE_KEY, theme);
  syncPwaChromeMeta(theme);
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: readStoredTheme(),
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next });
  },
}));

applyTheme(readStoredTheme());
